/// <amd-module name="Wit/DeliveryPlans/Views/PlanView" />

import * as React from "react";
import { getTFSProject } from "Tfs/Platform/Page";
import { IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled, IVssFeatureService } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssNavigationService } from "VSS/Platform/Navigation";
import { IVssPageService } from "VSS/Platform/Page";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Button } from "VSSUI/Button";
import { Card } from "VSSUI/Card";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Header, TitleSize } from "VSSUI/Header";
import { getFilterItem, getFilterItemIconProps, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Spinner } from "VSSUI/Spinner";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { Filter, FILTER_CHANGE_EVENT, IFilter } from "VSSUI/Utilities/Filter";
import {
    CardFieldSettings,
    DeliveryViewData,
    Marker,
    ParentChildWIMap,
    Rule,
    TimelineIterationStatusCode,
    TimelineTeamData,
    TimelineTeamStatusCode
} from "Wit/Clients/Work/Contracts/Work";
import { ICardStyle } from "Wit/ClientWorkItemService/IClientWorkItem";
import { ClientWorkItemServiceEvent } from "Wit/ClientWorkItemService/IClientWorkItemService";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames } from "Wit/Common/Generated/Constants";
import { IFieldEntry } from "Wit/Common/Generated/Contracts";
import { IWorkItemFieldService } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService";
import { IWorkItemFieldService2 } from "Wit/Common/WorkItemFieldService/IWorkItemFieldService2";
import { IDeliveryPlanShortcutService } from "Wit/DeliveryPlans/Common/Services/DeliveryPlanShortcutService";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { AssignedToFilterProvider } from "Wit/Filter/AssignedToFilterProvider";
import { FieldFilterProvider } from "Wit/Filter/FieldFilterProvider";
import { FilterManager, IParentFilterOperators, mapHistoryStateToFilterState } from "Wit/Filter/FilterManager";
import {
    AssignedToFilterValueProvider,
    ParentItemFilterValueProvider,
    StateFilterValueProvider,
    WorkItemTypeFilterValueProvider
} from "Wit/Filter/FilterValueProviders";
import { ParentFilterProvider } from "Wit/Filter/ParentFilterProvider";
import { TagsFilterProvider } from "Wit/Filter/TagsFilterProvider";
import { TextFilterProvider, TextFilterProviderWithUnassigned } from "Wit/Filter/TextFilterProvider";
import {
    DataSourceFilterValueProvider,
    generateDefaultFilterState,
    IWorkItemFilterField,
    mapToFilterState,
    resolveFilterState,
    WorkItemFilterFieldType
} from "Wit/Filter/WorkItemFilter";
import { BoardsMessages } from "Wit/Messages/Components/BoardsMessages";
import { WorkItemTypeColorAndIcon } from "Wit/UI/Components/WorkItemTypeIcon/WorkItemTypeIcon.Props";
import { INamedFieldDataDictionary } from "Wit/WorkItemDataManager/Contracts";
import { BoardConstants } from "../Board/BoardConstants";
import { Board } from "../Board/Components/Board";
import { IDeliveryPlansBoardLayoutService } from "../Board/Services/DeliveryPlansBoardLayoutService";
import { PlanData } from "../Common/Client/Contracts/DataProvider";
import { PlanFilterBar } from "../Common/Components/PlanFilterBar";
import { ClientFields, Features, FieldRefNames } from "../Common/Constants";
import { reportDataProviderError, reportError, reportWarning } from "../Common/ErrorHandling";
import { IDeliveryPlansWorkItem, IIteration, IObservableDeliveryPlansWorkItem, ITeam } from "../Common/Interfaces";
import { IDeliveryPlansIterationService } from "../Common/Services/DeliveryPlansIterationService";
import { IDeliveryPlansTeamService } from "../Common/Services/DeliveryPlansTeamService";
import { IDeliveryPlansWorkItemService, IWorkItemUpdatedEvent } from "../Common/Services/IDeliveryPlansWorkItemService";
import { IPlanService, PlanServiceEvents } from "../Common/Services/IPlanService";
import { getDateInUTC, ISpan } from "../Common/Utils/DateUtils";
import { publishTelemetry, TelemetryConstants } from "../Common/Utils/TelemetryUtils";
import { getWorkItemFromValues } from "../Common/WorkItem";
import { IDependencyService } from "../DependencyPanel/DependencyService";
import { PlanSettingsPanel, PlanSettingsPanelProps } from "../PlanSettingsPanel/Components/PlanSettingsPanel";

interface IPlanViewState {
    /**
     * Dictates if cards are collapsed or expanded
     */
    cardFieldsCollapsed: boolean;

    /**
     * Number representing the current scale of the delivery plan calendar view.
     * A higher value results in a more zoomed in view
     */
    scale: number;
}

const NewDeliveryPlansDataProviderKey = "ms.vss-work-web.work-item-new-delivery-plans-data-provider";

class PlanView extends VssComponent<{}, IPlanViewState> {
    private _timelineData: DeliveryViewData;
    private _planData: IObservableValue<PlanData | undefined>;
    private _filterFields: IWorkItemFilterField[] = [];
    private _filterToggled: IObservableValue<boolean>;
    private _filterManager: FilterManager;
    private _filter: IFilter;
    private _workItemTypeColorAndIconMap: { [witName: string]: WorkItemTypeColorAndIcon };
    private _isStakeholder: boolean;
    private _boardRef = React.createRef<Board>();
    private _virtualize: boolean;
    private _fieldReferenceNames: string[];
    private _tagStyles?: Map<string, string>;
    private _areAllDependenciesLoading: IObservableValue<boolean> = new ObservableValue(false);
    private _featureService: IVssFeatureService;
    private _deliveryPlanShortcutService: IDeliveryPlanShortcutService;
    /**
     * Dictates if all dependency lines are shown
     */
    private showAllDependencyLines: IObservableValue<boolean> = new ObservableValue(false);

    private _allDependencyLinesButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.ShowAllDependenciesLabel,
        iconProps: { iconName: "Link" },
        id: "all-depdencies-button",
        important: true,
        onActivate: () => {
            this.showAllDependencyLines.value = !this.showAllDependencyLines.value;
        },
        renderButton: props => {
            return (
                <div key="toggle-all-dependencies" className="relative">
                    <ConditionalChildren renderChildren={this._areAllDependenciesLoading}>
                        <div className="absolute-fill button-loading-overlay sub-layer" />
                        <div className="absolute-fill flex-column flex-center justify-center sub-layer">
                            <Spinner />
                        </div>
                    </ConditionalChildren>
                    <Button {...props} />
                </div>
            );
        }
    };

    private _collapseCardsButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.CollapseCardFieldsButtonAriaLabel,
        iconProps: { iconName: "ButtonControl" },
        id: "collapse-cards-button",
        important: true,
        onActivate: () => {
            const cardFieldsCollapsed = !this.state.cardFieldsCollapsed;
            this.setState({ cardFieldsCollapsed });
            this._updateCardFieldsCollapsedHistoryState(cardFieldsCollapsed);
            this._updateCommandBarCollapseCardsButton(cardFieldsCollapsed);
        }
    };

    private _zoomInButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.ZoomIn,
        id: "zoom-in",
        iconProps: { iconName: "ZoomIn" },
        important: true,
        onActivate: () => {
            let scale = BoardConstants.maxScale;
            for (let i = BoardConstants.scales.length - 1; i >= 0 && BoardConstants.scales[i] > this.state.scale; i--) {
                scale = BoardConstants.scales[i];
            }
            this._updateScaleState(scale);
            this._updateZoomHistoryState(scale);
            this._updateCommandBarZoomItems(scale);
        }
    };

    private _zoomOutButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.ZoomOut,
        id: "zoom-out",
        iconProps: { iconName: "ZoomOut" },
        important: true,
        onActivate: () => {
            let scale = BoardConstants.minScale;
            for (let i = 0; i < BoardConstants.scales.length && BoardConstants.scales[i] < this.state.scale; i++) {
                scale = BoardConstants.scales[i];
            }
            this._updateScaleState(scale);
            this._updateZoomHistoryState(scale);
            this._updateCommandBarZoomItems(scale);
        }
    };

    private _favoriteButton = {
        ariaLabel: Resources.FavoriteButtonAriaLabel,
        id: "favorite",
        iconProps: { iconName: "FavoriteStar" },
        onActivate: () => this._addFavorite(this._planData),
        subtle: true
    };

    private _unfavoriteButton = {
        ariaLabel: Resources.UnfavoriteButtonAriaLabel,
        className: "favorite-icon-color",
        id: "unfavorite",
        iconProps: { iconName: "FavoriteStarFill" },
        onActivate: () => this._removeFavorite(this._planData),
        subtle: true
    };

    private _fullScreenButton = {
        ariaLabel: Resources.FullScreen,
        iconProps: { iconName: "FullScreen" },
        id: "full-screen",
        onActivate: () => {
            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.toggleFullScreenMode();
            if (layoutManager.getFullScreenMode().value) {
                this._fullScreenButton.iconProps = { iconName: "BackToWindow" };
            } else {
                this._fullScreenButton.iconProps = { iconName: "FullScreen" };
            }
            this._headerCommandBarItems.change(this._headerCommandBarItems.value.indexOf(this._fullScreenButton), this._fullScreenButton);
        },
        subtle: true
    };

    private _toggleFilterButton: IHeaderCommandBarItem = getFilterItem(
        () => {
            this._filterToggled.value = !this._filterToggled.value;
        },
        /* isFiltering */ false,
        /* important */ true,
        Resources.Filter
    );

    private _cardCommandBarItems: ObservableArray<IHeaderCommandBarItem> = new ObservableArray();

    private _headerCommandBarItems: ObservableArray<IHeaderCommandBarItem> = new ObservableArray([this._toggleFilterButton, this._fullScreenButton]);

    public constructor(props: {}, context: IVssComponentContext) {
        super(props, context);

        const workItemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        this._deliveryPlanShortcutService = this.context.pageContext.getService<IDeliveryPlanShortcutService>("IDeliveryPlanShortcutService");
        this._workItemTypeColorAndIconMap = {};
        this._isStakeholder = !hasAgileToolsBacklogManagementPermission(context.pageContext);
        this._planData = new ObservableValue(undefined);

        if (!this._isStakeholder) {
            this._headerCommandBarItems.push({
                id: "plan-settings",
                onActivate: () => this._openNewPlanPanel(),
                text: Resources.SettingsButtonText
            });
        }

        const projectId = getTFSProject(this.context.pageContext)?.id || "";
        this._filterFields = [
            {
                displayType: WorkItemFilterFieldType.Text,
                fieldName: TextFilterProvider.PROVIDER_TYPE,
                placeholder: Resources.FilterByKeyword
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.WorkItemType,
                placeholder: Resources.FilterByTypes,
                noItemsText: Resources.FilterNoTypes,
                valueProvider: new WorkItemTypeFilterValueProvider(projectId, workItemService, this._workItemTypeColorAndIconMap)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.AssignedTo,
                placeholder: Resources.FilterByAssignedTo,
                valueProvider: new AssignedToFilterValueProvider(this.context.pageContext, workItemService)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.State,
                placeholder: Resources.FilterByStates,
                noItemsText: Resources.FilterNoStates,
                valueProvider: new StateFilterValueProvider(projectId, workItemService)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.AreaPath,
                placeholder: Resources.FilterByAreaPath,
                noItemsText: Resources.FilterNoArea,
                valueProvider: new DataSourceFilterValueProvider(CoreFieldRefNames.AreaPath, workItemService)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.IterationPath,
                placeholder: Resources.FilterByIteration,
                noItemsText: Resources.FilterNoIterations,
                valueProvider: new DataSourceFilterValueProvider(CoreFieldRefNames.IterationPath, workItemService)
            },
            {
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.Tags,
                placeholder: Resources.FilterByTags,
                noItemsText: Resources.FilterNoTags,
                valueProvider: new DataSourceFilterValueProvider(CoreFieldRefNames.Tags, workItemService),
                showOrAndOperators: true
            },
            {
                customOperators: [
                    { key: IParentFilterOperators.children, name: Resources.ParentFilterChildren },
                    { key: IParentFilterOperators.tree, name: Resources.ParentFilterTree }
                ],
                displayType: WorkItemFilterFieldType.CheckboxList,
                fieldName: CoreFieldRefNames.Parent,
                placeholder: Resources.FilterByParent,
                noItemsText: Resources.FilterNoParent,
                valueProvider: new ParentItemFilterValueProvider(this.context.pageContext, workItemService)
            }
        ] as IWorkItemFilterField[];

        this._filterManager = new FilterManager(workItemService);
        this._filter = new Filter();
        this._filter.setState(generateDefaultFilterState(this._filterFields));

        this._virtualize = isFeatureFlagEnabled(this.context.pageContext, Features.VirtualizeTimeline, false);

        this.state = {
            cardFieldsCollapsed: false,
            scale: BoardConstants.defaultScale
        };

        this._featureService = this.context.pageContext.getService<IVssFeatureService>("IVssFeatureService");
        const commandBarItems = [this._collapseCardsButton, this._zoomOutButton, this._zoomInButton];
        commandBarItems.unshift(this._allDependencyLinesButton);
        this._cardCommandBarItems.value = commandBarItems;
        this.showAllDependencyLines.subscribe(value => {
            queueMicrotask(() => {
                this._updateCommandBarAllDependencyLinesButton(value);
            });
        });
    }

    public async componentDidMount() {
        const pageService = this.context.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();

        const workItemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        workItemService.subscribe(this._onWorkItemChanged);

        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

        this._deliveryPlanShortcutService.registerShortcuts(
            () => (this._filterToggled.value = !this._filterToggled.value),
            () => layoutManager.toggleFullScreenMode(),
            (workItemId: number, onSave?: (workItem: any) => void) => workItemService.openWorkItemDialog(workItemId, onSave)
        );

        this._filter.subscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);
        this._filterManager.activate(this.context.pageContext);
        this._filterManager.clearFilterProviders();
        this._filterManager.registerFilterProvider(TextFilterProvider.PROVIDER_TYPE, new TextFilterProviderWithUnassigned());
        this._filterManager.registerFilterProvider(CoreFieldRefNames.WorkItemType, new FieldFilterProvider(CoreFieldRefNames.WorkItemType));
        this._filterManager.registerFilterProvider(CoreFieldRefNames.State, new FieldFilterProvider(CoreFieldRefNames.State));
        this._filterManager.registerFilterProvider(CoreFieldRefNames.AreaPath, new FieldFilterProvider(CoreFieldRefNames.AreaPath));
        this._filterManager.registerFilterProvider(CoreFieldRefNames.IterationPath, new FieldFilterProvider(CoreFieldRefNames.IterationPath));
        this._filterManager.registerFilterProvider(CoreFieldRefNames.Tags, new TagsFilterProvider());
        this._filterManager.registerFilterProvider(CoreFieldRefNames.Parent, new ParentFilterProvider());
        pageData && this._filterManager.registerFilterProvider(CoreFieldRefNames.AssignedTo, new AssignedToFilterProvider(pageData.user, this.context.pageContext));

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const initialFilterState = mapHistoryStateToFilterState(
            historyService.getState(),
            this._filterFields.map(f => f.fieldName)
        );

        const filterState = await resolveFilterState(this._filterFields, workItemService, initialFilterState);
        this._filter.setState(filterState);
        this._filterToggled = new ObservableValue(Object.keys(initialFilterState).length > 0);

        await this._loadPlan();
        this._setHistoryZoomState();
        this._setHistoryCardFieldsCollapsedState();

        if (this._planData.value!.isFavorite) {
            this._headerCommandBarItems.splice(0, 0, this._unfavoriteButton);
        } else {
            this._headerCommandBarItems.splice(0, 0, this._favoriteButton);
        }

        if(this._planData?.value?.name){
            document.title = this._planData?.value?.name
        };

        const properties: { [key: string]: any } = {
            [TelemetryConstants.TeamCount]: this._planData.value?.properties.teamBacklogMappings.length || 0
        };
        publishTelemetry(this.context.pageContext, TelemetryConstants.PlanViewed, properties);
    }

    public componentWillUnmount() {
        this._filterManager.clearFilterProviders();
        this._filterManager.deactivate();
        this._filter.unsubscribe(this._onFilterChanged, FILTER_CHANGE_EVENT);

        const workItemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        workItemService.unsubscribe(this._onWorkItemChanged);

        this._deliveryPlanShortcutService.unregisterShortcuts();

        const planService = this.context.pageContext.getService<IPlanService>("IPlanService");
        planService.unsubscribe(this._subscribeToPlanUpdates);
    }

    public render() {
        const { scale } = this.state;
        $DEBUG && console.log(`scale: ${scale}`);
        const workItemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        return (
            <Surface background={SurfaceBackground.neutral}>
                <Page className="flex-grow flex-column delivery-plan-page">
                    <BoardsMessages className="boards-message-bar flex-noshrink" />
                    <Observer
                        planData={this._planData}
                        filter={{
                            observableValue: this._filter,
                            filter: () => {
                                this._updateFilterButton();
                            }
                        }}
                    >
                        {(props: { planData: PlanData | undefined }) =>
                            props.planData ? (
                                <>
                                    <Header
                                        buttonCount={4}
                                        className={"delivery-plan-page-header"}
                                        commandBarItems={this._headerCommandBarItems}
                                        title={props.planData.name}
                                        description={props.planData.description || undefined}
                                        titleSize={TitleSize.Large}
                                    />
                                    <ConditionalChildren renderChildren={this._filterToggled}>
                                        <div className="page-content-top page-content-right page-content-left">
                                            <PlanFilterBar dataSource={workItemService} fields={this._filterFields} filter={this._filter} />
                                        </div>
                                    </ConditionalChildren>
                                    <div className="page-content page-content-top flex-grow flex-noshrink">
                                        <Card headerCommandBarItems={this._cardCommandBarItems} contentProps={{ className: "relative" }}>
                                            <Board
                                                canEdit={!this._isStakeholder}
                                                cardFieldsCollapsed={this.state.cardFieldsCollapsed}
                                                expanded={this._getHistoryTeamState(BoardConstants.allTeamsKey)}
                                                markers={props.planData.properties?.markers}
                                                onAddItem={this._onAddItem}
                                                onFetchMoreData={this._loadViewData}
                                                onItemMove={this._onItemMove}
                                                onLoadMoreWorkItems={this._onLoadMoreItems}
                                                onSaveChanges={this._saveChanges}
                                                ref={this._boardRef}
                                                scale={scale}
                                                showEmptyFields={props.planData.properties.cardSettings.fields.showEmptyFields}
                                                showChildRollup={props.planData.properties.cardSettings.fields.showChildRollup}
                                                showAllDependencyLines={this.showAllDependencyLines}
                                                tagStyles={this._tagStyles}
                                                teamCount={props.planData.properties.teamBacklogMappings.length}
                                                virtualizeTeams={this._virtualize}
                                                areAllDependenciesLoading={this._areAllDependenciesLoading}
                                            />
                                        </Card>
                                    </div>
                                </>
                            ) : null
                        }
                    </Observer>
                </Page>
            </Surface>
        );
    }

    private async _loadPlan() {
        const navigationService = this.context.pageContext.getService<IVssNavigationService>("IVssNavigationService");
        const routeValues = navigationService.getRouteValues();
        const planId = routeValues["plan"];
        const planService = this.context.pageContext.getService<IPlanService>("IPlanService");

        const plan = planService.getPlan(planId)!;
        this._updatePlan(plan);
        planService.subscribe(this._subscribeToPlanUpdates);
    }

    private _loadViewData = async (spanToLoad: ISpan) => {
        $DEBUG && console.log("fetching data for span: ", spanToLoad);
        const data = await this.getDataProviderData(this._planData.value!.id, undefined, spanToLoad.startDate, spanToLoad.endDate);
        $DEBUG && console.log("loaded timeline data: ", data);
        if (!data) {
            throw Error(`Failed to fetch data for span ${spanToLoad.startDate} to ${spanToLoad.endDate}`);
        }

        this._timelineData = data;
        const workItemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        const iterationService = this.context.pageContext.getService<IDeliveryPlansIterationService>("IDeliveryPlansIterationService");
        const teamService = this.context.pageContext.getService<IDeliveryPlansTeamService>("IDeliveryPlansTeamService");

        data.teams.forEach(t =>
            t.workItemTypeColors?.forEach(c => {
                if (!this._workItemTypeColorAndIconMap[c.workItemTypeName]) {
                    this._workItemTypeColorAndIconMap[c.workItemTypeName] = {
                        color: c.primaryColor,
                        icon: c.icon,
                        workItemTypeName: c.workItemTypeName,
                        isDisabled: false
                    };
                }
            })
        );

        const cardStyles = getCardStyles(this._planData.value?.properties.styleSettings);
        let fields : IFieldEntry[] | undefined = [];
        if (isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.CardsUseNewWorkItemFieldService, false)) {
            const fieldService = this.context.pageContext.getService<IWorkItemFieldService2>("IWorkItemFieldService2");
            fields = await fieldService.loadFields();
        } else {
            const fieldService = this.context.pageContext.getService<IWorkItemFieldService>("IWorkItemFieldService");
            fields = await fieldService.getFieldsAsync();
        }
        
        const violationsMap: { [id: number]: boolean | undefined } = {};
        const dependenciesMap: { [id: number]: boolean | undefined } = {};
        data.workItemViolations.forEach(id => (violationsMap[id] = true));
        data.workItemDependencies.forEach(id => (dependenciesMap[id] = true));
        const items: IObservableDeliveryPlansWorkItem[] = [];
        const parentMaps = data.parentItemMaps || [];
        const overlappingIterations = new Set<string>();
        const teams: ITeam[] = await Promise.all(
            data.teams.map(async t => {
                if (!this._fieldReferenceNames) {
                    this._fieldReferenceNames = t.fieldReferenceNames;
                }

                // add work items that don't belong to an iteration
                if (t.workItems) {
                    items.push(
                        ...((
                            await Promise.all(
                                t.workItems.map(async wi => {
                                    const item = await createWorkItem(
                                        this.context.pageContext,
                                        wi,
                                        t,
                                        iterationService,
                                        fields,
                                        parentMaps,
                                        violationsMap,
                                        dependenciesMap,
                                        cardStyles
                                    );

                                    // if this item doesn't have both a start and end date, we can't place it on the timeline
                                    // because its iteration isn't loaded, TODO
                                    if (!item.getFieldValue(FieldRefNames.StartDate) || !item.getFieldValue(FieldRefNames.TargetDate)) {
                                        $DEBUG && console.log("Found work item with missing start or end date: ", item);
                                        return;
                                    }

                                    return workItemService.add(item);
                                })
                            )
                        ).filter(wi => !!wi) as IObservableDeliveryPlansWorkItem[])
                    );
                }

                if (t.partiallyPagedWorkItems) {
                    items.push(
                        ...t.partiallyPagedWorkItems.map(wi =>
                            workItemService.add(createPartialWorkItem(wi, t, iterationService, fields, parentMaps, violationsMap, dependenciesMap))
                        )
                    );
                }

                const team = {
                    id: t.id,
                    name: t.name,
                    projectId: t.projectId,
                    backlog: t.backlog.categoryReferenceName,
                    backlogName: t.backlog.pluralName,
                    isExpanded: this._getHistoryTeamState(t.id + "-" + t.backlog.pluralName),
                    iterations: await Promise.all(
                        t.iterations?.map(async i => {
                            const iteration = {
                                id: i.cssNodeId,
                                name: i.name,
                                path: i.path,
                                startDate: getDateInUTC(i.startDate),
                                endDate: getDateInUTC(i.finishDate)
                            };

                            if (i.status.type === TimelineIterationStatusCode.IsOverlapping) {
                                overlappingIterations.add(iteration.name);
                            }

                            iterationService.addIteration(iteration);
                            if (i.workItems) {
                                items.push(
                                    ...(await Promise.all(
                                        i.workItems.map(async wi =>
                                            workItemService.add(
                                                await createWorkItem(
                                                    this.context.pageContext,
                                                    wi,
                                                    t,
                                                    iterationService,
                                                    fields,
                                                    parentMaps,
                                                    violationsMap,
                                                    dependenciesMap,
                                                    cardStyles
                                                )
                                            )
                                        )
                                    ))
                                );
                            }

                            if (i.partiallyPagedWorkItems) {
                                items.push(
                                    ...i.partiallyPagedWorkItems.map(wi =>
                                        workItemService.add(
                                            createPartialWorkItem(wi, t, iterationService, fields, parentMaps, violationsMap, dependenciesMap)
                                        )
                                    )
                                );
                            }

                            return iteration;
                        }) || []
                    ),
                    orderByField: t.orderByField,
                    rollupWorkItemTypes: t.rollupWorkItemTypes,
                    teamFieldName: t.teamFieldName,
                    teamFieldDefaultValue: t.teamFieldDefaultValue,
                    teamFieldValues: t.teamFieldValues || [],
                    // convert list of WorkItemColors to a dictionary of WorkItemTypeColorAndIcons
                    workItemTypeColorAndIcons: t.workItemTypeColors
                        ? Object.assign(
                              {},
                              ...t.workItemTypeColors.map(c => ({
                                  [c.workItemTypeName]: { color: c.primaryColor, icon: c.icon, workItemTypeName: c.workItemTypeName }
                              }))
                          )
                        : {}
                };

                return team;
            })
        );

        teamService.addTeams(teams);
        this._filterManager.dataUpdated();

        const layoutService = this.context.pageContext.getService<IDeliveryPlansBoardLayoutService>("IDeliveryPlansBoardLayoutService");
        layoutService.setInputData({
            coreFields: this._planData.value!.properties.cardSettings.fields.coreFields,
            additionalFields: this._planData.value!.properties.cardSettings.fields.additionalFields,
            teams,
            items,
            visibleIds: this._filterManager.filter(this.context.pageContext)
        });

        if (overlappingIterations.size) {
            reportWarning(this.context.pageContext, format(Resources.OverlappingIterationsWarning, [...overlappingIterations].join(", ")));
        }
    };

    private _onItemMove = async (
        observableItem: IObservableDeliveryPlansWorkItem,
        iteration: IIteration,
        team: ITeam,
        before: IDeliveryPlansWorkItem | undefined,
        after: IDeliveryPlansWorkItem | undefined
    ) => {
        const itemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        const teamService = this.context.pageContext.getService<IDeliveryPlansTeamService>("IDeliveryPlansTeamService");
        let forceReorderSave = false;

        const item = observableItem.value;
        // find the team this item belongs to
        const teams = teamService.getTeamsFromWorkItem(item);
        if (!teams.some(t => t.id === team.id)) {
            // changing teams is not allowed
            return;
        }

        if (iteration.path !== item.getFieldValue(CoreFieldRefNames.IterationPath)) {
            // iteration changed
            const changes: INamedFieldDataDictionary = {
                [CoreFieldRefNames.IterationPath]: iteration.path
            };
            const oldIteration = item.iteration;
            let startDate = item.getFieldValue<Date>(FieldRefNames.StartDate);
            let targetDate = item.getFieldValue<Date>(FieldRefNames.TargetDate);
            if (startDate) {
                const diff = oldIteration.startDate.getTime() - startDate.getTime();
                startDate = new Date(iteration.startDate.getTime() - diff);
                changes[FieldRefNames.StartDate] = startDate;
            }
            if (targetDate) {
                const diff = oldIteration.endDate.getTime() - targetDate.getTime();
                targetDate = new Date(iteration.endDate.getTime() - diff);
                changes[FieldRefNames.TargetDate] = targetDate;
            }

            if (before || after) {
                // reorder in the new iteration without saving to server so that the item is in the right place while we save the iteration change
                await itemService.reorderWorkItems(
                    observableItem.value.id,
                    team.projectId,
                    team.id,
                    before?.id,
                    after?.id,
                    iteration.path,
                    {
                        saveToServer: false
                    },
                    team.orderByField,
                    true /*updateStackRank*/,
                    iteration.id
                );
                forceReorderSave = true;
            }

            // we need to make sure this finishes before we try to reorder
            await saveChanges(this.context.pageContext, item, changes);
        }

        if (before || after) {
            // reorder
            await itemService.reorderWorkItems(
                observableItem.value.id,
                team.projectId,
                team.id,
                before?.id,
                after?.id,
                iteration.path,
                {
                    saveToServerEvenIfAlreadySorted: forceReorderSave
                },
                team.orderByField,
                true /*updateStackRank*/,
                iteration.id
            );
        }
    };

    /** callback for when the user adds a new item to the board */
    private _onAddItem = async (team: ITeam, iteration: IIteration, title: string, witType: string, direction: "top" | "bottom") => {
        const itemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        itemService.addNewWorkItem(team.projectId, witType, title, team, iteration, {}, direction);
    };

    private _onLoadMoreItems = async (project: string, workItemIds: number[]): Promise<void> => {
        const itemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        await itemService.loadWorkItems(project, workItemIds, this._fieldReferenceNames);

        // trigger a layout update once we've loaded all of the work items
        const layoutService = this.context.pageContext.getService<IDeliveryPlansBoardLayoutService>("IDeliveryPlansBoardLayoutService");
        this._filterManager.dataUpdated();
        layoutService.updateLayout(this._filterManager.filter(this.context.pageContext));
    };

    private _saveChanges = async (itemId: number, changes: { [fieldName: string]: any }) => {
        const itemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
        const item = itemService.get(itemId)?.value;
        if (!item) {
            throw Error("Could not find item.");
        }
        // make sure there's an actual change to save
        const changesExist = Object.keys(changes).some(field => {
            const a = changes[field];
            const b = item.getFieldValue(field);
            // TODO: there's gotta be a library function for this
            if (a instanceof Date && b instanceof Date) {
                return a.getTime() !== b.getTime();
            } else {
                return a !== b;
            }
        });
        if (changesExist) {
            saveChanges(this.context.pageContext, itemId, changes);
        }
    };

    private _openNewPlanPanel = () => {
        if (!this._planData.value || !this._timelineData) {
            return;
        }

        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            return <PlanSettingsPanel {...getPlanSettingsPanelProps(onDismiss, this._planData.value!, this._timelineData.teams)} />;
        });
    };

    private _subscribeToPlanUpdates = (planData: PlanData, action: string) => {
        if (action === PlanServiceEvents.planUpdate) {
            this._updatePlan(planData);
        }
    };

    private _updatePlan(planData: PlanData): void {
        planData.properties.markers = this._normalizeMarkers(planData.properties.markers);
        this._tagStyles = new Map<string, string>();
        for (const rule of planData.properties.tagStyleSettings ?? []) {
            const backgroundColor = (rule.settings as any)["background-color"];
            if (rule.isEnabled.toLowerCase() === "true" && backgroundColor) {
                this._tagStyles.set(rule.name, backgroundColor);
            }
        }

        this._planData.value = planData;
    }

    private _updateCommandBarZoomItems(newScale: number) {
        const zoomOutDisabled = newScale === BoardConstants.minScale;
        if (zoomOutDisabled !== this._zoomOutButton.disabled) {
            this._zoomOutButton.disabled = zoomOutDisabled;
            this._cardCommandBarItems.change(this._cardCommandBarItems.value.indexOf(this._zoomOutButton), this._zoomOutButton);
        }
        const zoomInDisabled = newScale === BoardConstants.maxScale;
        if (zoomInDisabled !== this._zoomInButton.disabled) {
            this._zoomInButton.disabled = zoomInDisabled;
            this._cardCommandBarItems.change(this._cardCommandBarItems.value.indexOf(this._zoomInButton), this._zoomInButton);
        }
    }

    private _updateCommandBarCollapseCardsButton(collapsed: boolean) {
        if (collapsed) {
            this._collapseCardsButton.iconProps = { className: "rotate-180", iconName: "PreviewLink" };
        } else {
            this._collapseCardsButton.iconProps = { className: undefined, iconName: "ButtonControl" };
        }
        this._collapseCardsButton.tooltipProps = {
            text: collapsed ? Resources.ExpandCardFieldsButtonAriaLabel : Resources.CollapseCardFieldsButtonAriaLabel
        };
        this._cardCommandBarItems.change(this._cardCommandBarItems.value.indexOf(this._collapseCardsButton), this._collapseCardsButton);
    }

    private _updateCommandBarAllDependencyLinesButton(showAllDependencyLines: boolean) {
        if (showAllDependencyLines) {
            this._allDependencyLinesButton.iconProps = { className: "rotate-180", iconName: "RemoveLink" };
        } else {
            this._allDependencyLinesButton.iconProps = { className: undefined, iconName: "Link" };
        }
        this._allDependencyLinesButton.tooltipProps = {
            text: showAllDependencyLines ? Resources.HideAllDependenciesLabel : Resources.ShowAllDependenciesLabel
        };
        this._cardCommandBarItems.change(this._cardCommandBarItems.value.indexOf(this._allDependencyLinesButton), this._allDependencyLinesButton);
    }

    private _addFavorite(planData: IObservableValue<PlanData | undefined>) {
        if (planData && planData.value) {
            const planService = this.context.pageContext.getService<IPlanService>("IPlanService");
            planService.favoritePlan(planData).then(() => {
                if (planData.value && !planData.value.isFavorite) {
                    this._headerCommandBarItems.splice(0, 1, this._favoriteButton);
                }
            });
            this._headerCommandBarItems.splice(0, 1, this._unfavoriteButton);
        }
    }

    private _removeFavorite(planData: IObservableValue<PlanData | undefined>) {
        if (planData && planData.value) {
            const planService = this.context.pageContext.getService<IPlanService>("IPlanService");
            planService.unfavoritePlan(planData).then(() => {
                if (planData.value && planData.value.isFavorite) {
                    this._headerCommandBarItems.splice(0, 1, this._unfavoriteButton);
                }
            });
            this._headerCommandBarItems.splice(0, 1, this._favoriteButton);
        }
    }

    private _updateZoomHistoryState(scale: number) {
        if (!this._planData.value?.id) {
            return;
        }

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const entries: { [key: string]: any } = { [BoardConstants.zoomKey + "/" + this._planData.value.id]: scale };
        settingsService.setEntries(entries, SettingsUserScope.Me);
    }

    private _updateCardFieldsCollapsedHistoryState(collapsed: boolean) {
        if (!this._planData.value?.id) {
            return;
        }

        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const entries: { [key: string]: any } = { [BoardConstants.cardFieldsCollapsedKey + "/" + this._planData.value.id]: collapsed };
        settingsService.setEntries(entries, SettingsUserScope.Me);
    }

    private _setHistoryZoomState() {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const setting: { [key: string]: string } | undefined = settingsService.getEntry(BoardConstants.zoomKey, SettingsUserScope.Me);
        if (setting && this._planData.value && setting[this._planData.value.id]) {
            const scale = parseFloat(setting[this._planData.value.id]);
            this._updateScaleState(scale);
            this._updateCommandBarZoomItems(scale);
        }
    }

    private _setHistoryCardFieldsCollapsedState() {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const setting: { [key: string]: boolean } | undefined = settingsService.getEntry(BoardConstants.cardFieldsCollapsedKey, SettingsUserScope.Me);
        if (setting && this._planData.value && setting[this._planData.value.id]) {
            const cardFieldsCollapsed = setting[this._planData.value.id];
            this.setState({ cardFieldsCollapsed });
            this._updateCommandBarCollapseCardsButton(cardFieldsCollapsed);
        }
    }

    private _getHistoryTeamState(teamName: string): boolean {
        const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
        const setting: { [key: string]: string } | undefined = settingsService.getEntry(BoardConstants.teamKey, SettingsUserScope.Me);
        let teamSetting: string | undefined = undefined;
        if (setting) {
            const allTeamsSetting = setting[BoardConstants.allTeamsKey];
            teamSetting = allTeamsSetting !== undefined ? allTeamsSetting : setting[teamName];
        }
        return teamSetting != undefined ? !!teamSetting : true;
    }

    private _updateScaleState(newScale: number) {
        this._boardRef.current?.shiftTimelineBeforeScaleChange(newScale);
        this.setState({ scale: newScale });
    }

    private async getDataProviderData(id: string, revision?: number, startDate?: Date, endDate?: Date): Promise<DeliveryViewData | undefined> {
        const data = {
            Id: id,
            Revision: revision,
            StartDate: startDate,
            EndDate: endDate
        };
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const result = await contributionService.getDataAsync<DeliveryViewData>(NewDeliveryPlansDataProviderKey, data, true);
        if (!result) {
            reportDataProviderError(this.context.pageContext, "PlanView.getDataProviderData", NewDeliveryPlansDataProviderKey);
        } else if (result.teams) {
            result.teams.forEach(e => {
                if (e.status !== undefined && e.status.type !== undefined && e.status.type != TimelineTeamStatusCode.OK) {
                    reportError(this.context.pageContext, "PlanView.getDataProviderData", e.status.message);
                }
            })
        }
        return result;
    }

    private _normalizeMarkers(markers: Marker[] | undefined): Marker[] {
        if (!markers) {
            return [];
        }

        return markers.map(marker => {
            marker.date = getDateInUTC(marker.date);
            return marker;
        });
    }

    private _onFilterChanged = () => {
        const layoutService = this.context.pageContext.getService<IDeliveryPlansBoardLayoutService>("IDeliveryPlansBoardLayoutService");
        this._filterManager.setFilters(mapToFilterState(this._filter.getState()));
        layoutService.updateLayout(this._filterManager.filter(this.context.pageContext));
    };

    private _updateFilterButton = (): void => {
        const filterButtonIdx = this._headerCommandBarItems.value.indexOf(this._toggleFilterButton);
        if (filterButtonIdx === -1) {
            return;
        }
        const isFiltering = this._filterManager.isFiltering();
        this._toggleFilterButton.iconProps = getFilterItemIconProps(isFiltering);
        this._headerCommandBarItems.change(filterButtonIdx, this._toggleFilterButton);
    };

    /** handle when an item in the work item store changes */
    private _onWorkItemChanged = (e: IWorkItemUpdatedEvent, action?: ClientWorkItemServiceEvent | undefined) => {
        const layoutService = this.context.pageContext.getService<IDeliveryPlansBoardLayoutService>("IDeliveryPlansBoardLayoutService");
        if (e.item) {
            const workItemService = this.context.pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
            const observableItem = workItemService.get(e.item.id);
            if (observableItem) {
                const teamService = this.context.pageContext.getService<IDeliveryPlansTeamService>("IDeliveryPlansTeamService");
                const teams = teamService.getTeamsFromWorkItem(observableItem.value);
                const workItemTeam = teams?.[0];
                if (action === ClientWorkItemServiceEvent.NewWorkItemAdded) {
                    layoutService.addItems([observableItem]);
                } else if (action === ClientWorkItemServiceEvent.WorkItemUpdated) {
                    observableItem.updateBackgroundColor(
                        this.context.pageContext,
                        getCardStyles(this._planData.value?.properties.styleSettings),
                        workItemTeam?.id
                    );
                    // todo: is there a way to determine if the update contained relevant link changes?
                    const dependencyService = this.context.pageContext.getService<IDependencyService>("IDependencyService");
                    dependencyService.clear();
                } else if (action === ClientWorkItemServiceEvent.PartialWorkItemUpdated) {
                    observableItem.updateBackgroundColor(
                        this.context.pageContext,
                        getCardStyles(this._planData.value?.properties.styleSettings),
                        workItemTeam?.id
                    );
                    return;
                }
            }
        }

        this._filterManager.dataUpdated();
        layoutService.updateLayout(this._filterManager.filter(this.context.pageContext));
    };
}

Components.add("delivery-plans-plan-view-component", PlanView);

async function saveChanges(pageContext: IVssPageContext, item: IDeliveryPlansWorkItem | number, changes: INamedFieldDataDictionary): Promise<void> {
    const itemService = pageContext.getService<IDeliveryPlansWorkItemService>("IDeliveryPlansWorkItemService");
    await itemService.updateWorkItem(item, changes);
}

function getPlanSettingsPanelProps(onDismiss: () => void, plan: PlanData, teams: TimelineTeamData[]): PlanSettingsPanelProps {
    return {
        cardFieldSettings: plan.properties.cardSettings.fields ?? ({} as CardFieldSettings),
        fieldCriteria: plan.properties.criteria ?? [],
        markerSettings: plan.properties.markers ?? [],
        styleSettings: plan.properties.styleSettings ?? [],
        tagStyleSettings: plan.properties.tagStyleSettings ?? [],
        onDismiss,
        plan,
        teams
    };
}

function hasAgileToolsBacklogManagementPermission(pageContext: IVssPageContext): boolean {
    const pageDataService = pageContext.getService<IVssContributionService>("IVssContributionService");
    const permissionData = pageDataService.getData<{ hasPermission: boolean }>("ms.vss-work-web.backlog-permission-data-provider");
    return !!permissionData && permissionData.hasPermission;
}

function createPartialWorkItem(
    fieldValues: any[],
    teamData: TimelineTeamData,
    iterationService: IDeliveryPlansIterationService,
    fields: IFieldEntry[] | undefined,
    parentMaps: ParentChildWIMap[],
    violationsMap: {
        [id: number]: boolean | undefined;
    },
    dependenciesMap: {
        [id: number]: boolean | undefined;
    }
) {
    const item = getWorkItemFromValues(fieldValues, teamData.partiallyPagedFieldReferenceNames, iterationService, fields, parentMaps, true);
    const hasDependencyError = violationsMap[item.id];
    const hasDependency = dependenciesMap[item.id];
    item.setFieldValue(ClientFields.HasDependencyError, !!hasDependencyError);
    item.setFieldValue(ClientFields.HasDependency, !!hasDependency);
    item.setFieldValue(teamData.teamFieldName, teamData.teamFieldDefaultValue);
    return item;
}

async function createWorkItem(
    pageContext: IVssPageContext,
    fieldValues: any[],
    teamData: TimelineTeamData,
    iterationService: IDeliveryPlansIterationService,
    fields: IFieldEntry[] | undefined,
    parentMaps: ParentChildWIMap[],
    violationsMap: {
        [id: number]: boolean | undefined;
    },
    dependenciesMap: {
        [id: number]: boolean | undefined;
    },
    styleSettings: ICardStyle[] | undefined
) {
    const item = getWorkItemFromValues(fieldValues, teamData.fieldReferenceNames, iterationService, fields, parentMaps);
    await item.updateBackgroundColor(pageContext, styleSettings, teamData.id);
    const hasDependencyError = violationsMap[item.id];
    const hasDependency = dependenciesMap[item.id];
    item.setFieldValue(ClientFields.HasDependencyError, !!hasDependencyError);
    item.setFieldValue(ClientFields.HasDependency, !!hasDependency);
    return item;
}

function getCardStyles(rules: Rule[] | undefined): ICardStyle[] | undefined {
    return rules?.map(rule => ({
        name: rule.name,
        backgroundColor: (rule.settings as any)["background-color"],
        clauses: rule.clauses,
        isEnabled: rule.isEnabled.toLowerCase() === "true",
        titleStyle: {
            isTitleBold: (rule.settings as any)["title-font-weight"],
            isTitleItalicized: (rule.settings as any)["title-font-style"],
            isTitleUnderlined: (rule.settings as any)["title-text-decoration"],
            titleColor: (rule.settings as any)["title-color"]
        }
    }));
}
